<template>
  <div class="community-post-composer">
    <button
      v-if="!expandedComposer"
      class="composer-collapsed d-flex align-items-center"
      @click="expandedComposer = true"
    >
      <div class="composer-avatar">
        <img v-if="userPhoto" :src="userPhoto" :alt="userName" />
        <span v-else class="letter">{{ userInitial }}</span>
      </div>
      <span class="composer-placeholder">Criar uma publicação</span>
    </button>

    <template v-else>
      <rich-text-editor
        v-model="text"
        placeholder="Compartilhe algo com a comunidade..."
      />

      <div v-if="image" class="composer-image mt-2">
        <img :src="image" alt="" />
        <button type="button" class="btn-remove-image" @click="image = null">
          Remover imagem
        </button>
      </div>

      <post-producer-controls
        v-if="showProducerControls"
        :publish-at.sync="publishAt"
        :is-pinned.sync="isPinned"
        :moderator-profile-code.sync="moderatorProfileCode"
      />

      <div
        class="composer-actions d-flex align-items-center justify-content-between mt-2"
      >
        <input-image id="composer-image" v-model="image">
          <span class="btn-attach">
            <i class="icon-image" /> Adicionar imagem
          </span>
        </input-image>

        <div>
          <button class="btn btn-outline-secondary mr-2" @click="collapse">
            Cancelar
          </button>
          <button
            class="btn btn-primary px-4"
            :disabled="loading || !canSubmit"
            @click="submit"
          >
            <loading :show="loading" small>{{ submitLabel }}</loading>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import InputImage from "@/components/InputImage.vue";
import RichTextEditor from "./RichTextEditor.vue";
import PostProducerControls from "./PostProducerControls.vue";

export default {
  name: "PostComposer",
  components: { InputImage, RichTextEditor, PostProducerControls },
  props: {
    communityId: {
      type: String,
      required: true
    },
    canModerate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      text: "",
      image: null,
      publishAt: null,
      isPinned: false,
      moderatorProfileCode: null,
      loading: false,
      expandedComposer: false
    };
  },
  computed: {
    user() {
      return (this.$user && this.$user.user) || {};
    },
    userName() {
      return this.user.name || "Você";
    },
    userPhoto() {
      return this.user.photo;
    },
    userInitial() {
      return (this.userName[0] || "?").toUpperCase();
    },
    showProducerControls() {
      return this.canModerate;
    },
    canSubmit() {
      return !!(this.text && this.text.trim()) || !!this.image;
    },
    isScheduled() {
      return !!this.publishAt && new Date(this.publishAt) > new Date();
    },
    submitLabel() {
      return this.isScheduled ? "Agendar" : "Publicar";
    }
  },
  methods: {
    submit() {
      if (!this.canSubmit) return;
      this.loading = true;
      let action;
      let payload;
      if (this.canModerate) {
        action = "community/createProducerPost";
        payload = {
          id: this.communityId,
          text: this.text,
          image: this.image,
          publish_at: this.publishAt,
          is_pinned: this.isPinned,
          moderator_profile_code: this.moderatorProfileCode
        };
      } else {
        action = "community/createPost";
        payload = {
          id: this.communityId,
          text: this.text,
          image: this.image
        };
      }
      this.$store
        .dispatch(action, payload)
        .then(post => {
          this.$message.success(
            this.isScheduled ? "Publicação agendada" : "Publicação criada"
          );
          this.reset();
          this.expandedComposer = false;
          this.$emit("created", post);
        })
        .catch(error => {
          const message =
            error?.response?.data?.non_field_errors?.[0] ||
            error?.response?.data?.moderator_profile_code?.[0] ||
            error?.response?.data?.publish_at?.[0] ||
            "Não foi possível publicar";
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    collapse() {
      this.reset();
      this.expandedComposer = false;
    },
    reset() {
      this.text = "";
      this.image = null;
      this.publishAt = null;
      this.isPinned = false;
      this.moderatorProfileCode = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.community-post-composer {
  padding: 16px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  background: rgba(128, 128, 128, 0.06);
  font-family: "Inter", "Noto Sans", sans-serif;

  .composer-collapsed {
    width: 100%;
    border: none;
    background: transparent;
    padding: 0;
    color: inherit;
    gap: 12px;
    text-align: left;

    .composer-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(128, 128, 128, 0.2);
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .letter {
        font-size: 14px;
        font-weight: 500;
      }
    }

    .composer-placeholder {
      font-size: 14px;
      line-height: 20px;
      opacity: 0.7;
    }

    &:hover .composer-placeholder {
      opacity: 1;
    }
  }

  .composer-image {
    position: relative;
    display: inline-block;

    img {
      max-width: 100%;
      max-height: 240px;
      border-radius: 8px;
    }

    .btn-remove-image {
      display: block;
      margin-top: 4px;
      border: none;
      background: transparent;
      color: #ff757e;
      font-size: 12px;
      padding: 0;
    }
  }

  .btn-attach {
    display: inline-flex;
    align-items: center;
    font-size: 14px;

    i {
      font-size: 18px;
      margin-right: 6px;
    }
  }
}

.theme-dark .community-post-composer {
  background: #0d161c;
  border-color: #07151f;

  .composer-collapsed {
    .composer-avatar {
      background: #54acbe;
      border: 1px solid #438a98;

      .letter {
        color: #2c3942;
      }
    }

    .composer-placeholder {
      color: #7a8c99;
      opacity: 1;
    }
  }
}
</style>
